import React, { useState } from 'react';
import { DatePicker } from '../ui/DatePicker';

interface StaffInfo {
    totalClearers: number;
    lhrEligible: number;
    lgwEligible: number;
    driverEligible: number;
    warnings: string[];
}

interface QuickConfigStepProps {
    startDate: string;
    endDate: string;
    config: QuickConfig;
    onChange: (config: QuickConfig) => void;
    onBack: () => void;
    onGenerate: () => void;
    loading?: boolean;
    staffInfo?: StaffInfo;
    controllers?: Array<{ id: string; name: string; email?: string }>;
}

export interface QuickConfig {
    // LHR Settings
    heathrowClearerCount: number;
    minimumDrivers: number;

    // LGW Settings
    lgwClearerCount: number;

    // Controller Settings
    controllerCount: number;
    teamAControllerId: string;
    controllerAnchorDate: string; // Last ON day for Team A

    // Advanced
    allowAgencyStaff: boolean;
    busyDates: string[]; // High volume dates
}

const QuickConfigStep: React.FC<QuickConfigStepProps> = ({
    startDate,
    endDate,
    config,
    onChange,
    onBack,
    onGenerate,
    loading = false,
    staffInfo,
    controllers = []
}) => {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [pendingBusyDate, setPendingBusyDate] = useState('');

    const updateConfig = (field: keyof QuickConfig, value: any) => {
        onChange({ ...config, [field]: value });
    };

    return (
        <div className="space-y-6">
            <h3 className="font-semibold text-lg">Step 2: Quick Configuration</h3>
            <p className="text-sm text-gray-600">
                Configure staff requirements for {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
            </p>

            {/* Staff Coverage Panel */}
            {staffInfo && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-bold text-blue-800 mb-2">📊 Available Staff</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div className="flex items-center gap-1">
                            <span className="font-medium">Total Clearers:</span>
                            <span className={staffInfo.totalClearers === 0 ? 'text-red-600 font-bold' : 'text-gray-700'}>
                                {staffInfo.totalClearers}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="font-medium">LHR Skilled:</span>
                            <span className={staffInfo.lhrEligible === 0 ? 'text-amber-600' : 'text-green-700'}>
                                {staffInfo.lhrEligible === 0 ? `0 (all ${staffInfo.totalClearers} will be used)` : staffInfo.lhrEligible}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="font-medium">LGW Skilled:</span>
                            <span className={staffInfo.lgwEligible === 0 ? 'text-amber-600' : 'text-green-700'}>
                                {staffInfo.lgwEligible === 0 ? `0 (all ${staffInfo.totalClearers} will be used)` : staffInfo.lgwEligible}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="font-medium">Drivers:</span>
                            <span className={staffInfo.driverEligible === 0 ? 'text-amber-600' : 'text-green-700'}>
                                {staffInfo.driverEligible === 0 ? 'None (skill check bypassed)' : staffInfo.driverEligible}
                            </span>
                        </div>
                    </div>
                    {staffInfo.warnings.length > 0 && (
                        <div className="space-y-1">
                            {staffInfo.warnings.map((w, i) => (
                                <p key={i} className="text-xs text-amber-700 flex items-start gap-1">
                                    <span className="font-bold">!</span><span>{w}</span>
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Controllers Section */}
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
                    🎮 Controllers
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Number of Controllers</label>
                        <input
                            type="number"
                            min={1}
                            max={4}
                            value={config.controllerCount}
                            onChange={(e) => updateConfig('controllerCount', parseInt(e.target.value) || 2)}
                            className="w-full border rounded px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">4-on-4-off alternating pattern</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Team A Controller</label>
                        <select
                            value={config.teamAControllerId}
                            onChange={(e) => updateConfig('teamAControllerId', e.target.value)}
                            className="w-full border rounded px-3 py-2 bg-white"
                        >
                            <option value="">Auto-select Team A</option>
                            {controllers.map((controller) => (
                                <option key={controller.id} value={controller.id}>
                                    {controller.name}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-orange-700 mt-1">
                            {controllers.find((controller) => controller.id === config.teamAControllerId)?.name || 'Selected controller'} is Team A
                        </p>
                    </div>
                    <div>
                        <DatePicker
                            label={`${controllers.find((controller) => controller.id === config.teamAControllerId)?.name || 'Team A'} Last ON Day`}
                            value={config.controllerAnchorDate}
                            onChange={(v) => updateConfig('controllerAnchorDate', v)}
                            placeholder="Pick anchor date"
                        />
                        <p className="text-xs text-gray-500 mt-1">For Team A pattern alignment</p>
                    </div>
                </div>
            </div>

            {/* Heathrow Section */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                    ✈️ Heathrow (LHR)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Clearers per Day</label>
                        <input
                            type="number"
                            min={1}
                            max={10}
                            value={config.heathrowClearerCount}
                            onChange={(e) => updateConfig('heathrowClearerCount', parseInt(e.target.value) || 3)}
                            className="w-full border rounded px-3 py-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Minimum Drivers</label>
                        <input
                            type="number"
                            min={1}
                            max={config.heathrowClearerCount}
                            value={config.minimumDrivers}
                            onChange={(e) => updateConfig('minimumDrivers', parseInt(e.target.value) || 2)}
                            className="w-full border rounded px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">Must have "can drive" skill</p>
                    </div>
                </div>
            </div>

            {/* Gatwick Section */}
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                    🛫 Gatwick (LGW)
                </h4>
                <div>
                    <label className="block text-sm font-medium mb-1">Clearers per Day</label>
                    <input
                        type="number"
                        min={1}
                        max={5}
                        value={config.lgwClearerCount}
                        onChange={(e) => updateConfig('lgwClearerCount', parseInt(e.target.value) || 1)}
                        className="w-24 border rounded px-3 py-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Dual-skilled staff prioritized</p>
                </div>
            </div>

            {/* Advanced Settings (Collapsed) */}
            <div className="border rounded-lg">
                <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="w-full px-4 py-3 text-left font-medium text-gray-700 flex justify-between items-center hover:bg-gray-50"
                >
                    ⚙️ Advanced Settings
                    <span>{showAdvanced ? '▲' : '▼'}</span>
                </button>
                {showAdvanced && (
                    <div className="p-4 border-t space-y-4">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={config.allowAgencyStaff}
                                onChange={(e) => updateConfig('allowAgencyStaff', e.target.checked)}
                            />
                            Allow Agency Staff
                        </label>
                        <div>
                            <label className="block text-sm font-medium mb-1">Busy / High Volume Dates</label>
                            <p className="text-xs text-gray-500 mb-2">Staff will be doubled on these dates</p>
                            <div className="flex items-end gap-2 mb-2">
                                <div className="flex-1">
                                    <DatePicker
                                        value={pendingBusyDate}
                                        onChange={setPendingBusyDate}
                                        min={startDate}
                                        max={endDate}
                                        placeholder="Pick busy date"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (pendingBusyDate && !config.busyDates.includes(pendingBusyDate)) {
                                            updateConfig('busyDates', [...config.busyDates, pendingBusyDate]);
                                            setPendingBusyDate('');
                                        }
                                    }}
                                    className="px-4 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                >
                                    Add
                                </button>
                            </div>
                            {config.busyDates.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {config.busyDates.sort().map(date => (
                                        <span key={date} className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                            {new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                            <button
                                                type="button"
                                                onClick={() => updateConfig('busyDates', config.busyDates.filter(d => d !== date))}
                                                className="text-red-600 hover:text-red-900 font-bold ml-1"
                                            >×</button>
                                        </span>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => updateConfig('busyDates', [])}
                                        className="text-xs text-gray-500 hover:text-red-600 ml-1"
                                    >
                                        Clear all
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-4 border-t">
                <button onClick={onBack} className="px-4 py-2 border rounded hover:bg-gray-50">
                    ← Back
                </button>
                <button
                    onClick={onGenerate}
                    disabled={loading}
                    className="px-6 py-2 bg-green-600 text-white rounded font-bold hover:bg-green-700 disabled:opacity-50"
                >
                    {loading ? 'Loading...' : 'Next →'}
                </button>
            </div>
        </div>
    );
};

export default QuickConfigStep;
